import { Tag } from '@curri/components'
import { Broadcast, Flask, SignIn, SignOut } from '@phosphor-icons/react'
import type { ReactNode } from 'react'

import { useAuth } from '../auth/AuthProvider.tsx'

// The session control, pinned to the bottom of the rail — where the product
// chrome puts its account row, and styled like it: nav rows with an icon and a
// label, not a boxed button. A button down here reads as the page's primary
// action, which signing in is not.
//
// The mode row mirrors the app's "Theme … System" shape: label on the left, the
// current value on the right. It reads the credential rather than offering a
// toggle — sandbox vs live is a property of the token (an api-key user carries
// `isSandbox`, an OAuth token carries the issuing client's environment), so
// switching modes means a different credential, not a UI switch.

const FooterRow = ({
  collapsed,
  icon,
  label,
  onClick,
  title,
  trailing,
}: {
  collapsed: boolean
  icon: ReactNode
  label: string
  onClick?: () => void
  title?: string
  trailing?: ReactNode
}) => {
  const interactive = Boolean(onClick)

  return (
    <button
      type="button"
      aria-label={collapsed ? label : undefined}
      disabled={!interactive}
      onClick={onClick}
      title={collapsed ? (title ?? label) : title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        height: 34,
        padding: collapsed ? 0 : '0 10px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        border: 0,
        borderRadius: 'var(--curri-radius-md)',
        background: 'transparent',
        cursor: interactive ? 'pointer' : 'default',
        fontFamily: 'inherit',
        fontSize: 13.5,
        fontWeight: 500,
        color: 'var(--curri-text-secondary)',
        textAlign: 'left',
      }}
    >
      {icon}
      {!collapsed && (
        <>
          <span style={{ flex: 1 }}>{label}</span>
          {trailing}
        </>
      )}
    </button>
  )
}

export const SessionControl = ({ collapsed }: { collapsed: boolean }) => {
  const { state, signIn, signOut } = useAuth()

  if (state.status === 'authenticated') {
    const { isSandbox } = state.caller

    return (
      <>
        <FooterRow
          collapsed={collapsed}
          icon={isSandbox ? <Flask size={17} /> : <Broadcast size={17} />}
          label="Mode"
          title={
            isSandbox
              ? 'This credential is scoped to sandbox'
              : 'This credential reaches live data'
          }
          trailing={
            <Tag
              color={isSandbox ? 'warning' : 'success'}
              label={isSandbox ? 'Sandbox' : 'Live'}
            />
          }
        />
        <FooterRow
          collapsed={collapsed}
          icon={<SignOut size={17} />}
          label="Sign out"
          onClick={signOut}
        />
      </>
    )
  }

  const signingIn = state.status === 'authenticating'

  return (
    <FooterRow
      collapsed={collapsed}
      icon={<SignIn size={17} />}
      label={signingIn ? 'Signing in…' : 'Sign in'}
      onClick={signingIn ? undefined : signIn}
    />
  )
}
